if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* JASZ: mobile adaptive height. Original gallery controls remain below. */
      connectedCallback() {
        if (this.jaszHeightReady || !this.elements.viewer || !this.elements.thumbnails) return;
        this.jaszHeightReady = true;
        this.jaszQueueHeight = () => {
          window.cancelAnimationFrame(this.jaszHeightFrame);
          this.jaszHeightFrame = window.requestAnimationFrame(() => this.jaszUpdateHeight());
        };
        this.jaszSlideChanged = (event) => {
          const slide = event.detail && event.detail.currentElement;
          if (slide && slide.classList.contains('product__media-item')) {
            this.jaszHeightSlide = slide;
            this.jaszQueueHeight();
          }
        };
        this.elements.viewer.addEventListener('slideChanged', this.jaszSlideChanged);
        this.elements.viewer.addEventListener('load', this.jaszQueueHeight, true);
        this.elements.viewer.addEventListener('loadedmetadata', this.jaszQueueHeight, true);
        window.addEventListener('resize', this.jaszQueueHeight);

        // Catch thumbnail/color selection, reordering and newly inserted media.
        this.jaszMediaObserver = new MutationObserver((records) => {
          const selectedSlideChanged = records.some((record) =>
            record.type === 'attributes' &&
            record.target.matches('.product__media-item.is-active')
          );
          if (selectedSlideChanged || !this.jaszHeightSlide || !this.contains(this.jaszHeightSlide)) {
            this.jaszHeightSlide = this.elements.viewer.querySelector('.product__media-item.is-active');
          }
          this.jaszQueueHeight();
        });
        this.jaszMediaObserver.observe(this.elements.viewer, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['class'],
        });

        if ('ResizeObserver' in window) {
          this.jaszSizeObserver = new ResizeObserver((entries) => {
            let needsUpdate = false;
            entries.forEach((entry) => {
              if (entry.target === this.elements.viewer) {
                if (this.jaszViewerWidth !== entry.contentRect.width) {
                  this.jaszViewerWidth = entry.contentRect.width;
                  needsUpdate = true;
                }
              } else {
                needsUpdate = true;
              }
            });
            if (needsUpdate) this.jaszQueueHeight();
          });
          this.jaszSizeObserver.observe(this.elements.viewer);
        }
        this.jaszQueueHeight();
      }

      disconnectedCallback() {
        if (!this.jaszHeightReady) return;
        window.cancelAnimationFrame(this.jaszHeightFrame);
        window.removeEventListener('resize', this.jaszQueueHeight);
        this.elements.viewer.removeEventListener('slideChanged', this.jaszSlideChanged);
        this.elements.viewer.removeEventListener('load', this.jaszQueueHeight, true);
        this.elements.viewer.removeEventListener('loadedmetadata', this.jaszQueueHeight, true);
        this.jaszMediaObserver.disconnect();
        if (this.jaszSizeObserver) this.jaszSizeObserver.disconnect();
        this.jaszObservedSlide = null;
        this.jaszHeightReady = false;
        this.jaszClearHeight();
      }

      jaszClearHeight() {
        this.removeAttribute('data-jasz-auto-height');
        if (this.jaszHeightList) this.jaszHeightList.style.removeProperty('--jasz-gallery-height');
      }

      jaszUpdateHeight() {
        if (!this.isConnected) return;
        const list = this.elements.viewer.querySelector('.product__media-list');
        const product = this.closest('.product');
        if (!list || this.mql.matches || (product && product.classList.contains('product--mobile-columns'))) {
          this.jaszClearHeight();
          return;
        }
        if (this.jaszHeightList && this.jaszHeightList !== list) {
          this.jaszHeightList.style.removeProperty('--jasz-gallery-height');
        }
        this.jaszHeightList = list;

        // Natural slide height must not inherit the tallest neighbor's height.
        this.setAttribute('data-jasz-auto-height', '');
        const visible = (element) => element && list.contains(element) && element.getBoundingClientRect().width > 0;
        let slide = this.jaszHeightSlide;
        if (!visible(slide)) slide = list.querySelector('.product__media-item.is-active');
        if (!visible(slide)) slide = Array.from(list.querySelectorAll('.product__media-item')).find(visible);
        if (!slide) {
          this.jaszClearHeight();
          return;
        }
        this.jaszHeightSlide = slide;
        if (this.jaszSizeObserver && this.jaszObservedSlide !== slide) {
          if (this.jaszObservedSlide) this.jaszSizeObserver.unobserve(this.jaszObservedSlide);
          this.jaszObservedSlide = slide;
          this.jaszSizeObserver.observe(slide);
        }

        const slideHeight = slide.getBoundingClientRect().height;
        if (slideHeight <= 0) return;
        const number = (value) => parseFloat(value) || 0;
        const itemStyle = window.getComputedStyle(slide);
        const listStyle = window.getComputedStyle(list);
        const height = Math.ceil(
          slideHeight + number(itemStyle.marginTop) + number(itemStyle.marginBottom) +
          number(listStyle.paddingTop) + number(listStyle.paddingBottom) +
          number(listStyle.borderTopWidth) + number(listStyle.borderBottomWidth)
        );
        const value = `${height}px`;
        if (list.style.getPropertyValue('--jasz-gallery-height') !== value) {
          list.style.setProperty('--jasz-gallery-height', value);
        }
      }
      /* End JASZ mobile adaptive height. */

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}